import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import { toast } from 'react-toastify';
import { createObject } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

// Import the app store icons (you'll need to add these to your project)
import appStoreIcon from '../images/app-store.png';
import googlePlayIcon from '../images/google-play.png';
import windowsStoreIcon from '../images/windows-store.png';
import macAppStoreIcon from '../images/mac-app-store.png';

const Home = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    description: ''
  });
  
  const [qrData, setQrData] = useState(null);
  const [seed, setSeed] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validare
    if (!formData.full_name || !formData.phone_number || !formData.description) {
      toast.error('Te rugăm să completezi toate câmpurile');
      return;
    }
    
    try {
      setLoading(true);
      
      // Apel către API pentru a crea un nou obiect
      const newObject = await createObject({
        full_name: formData.full_name,
        phone_number: formData.phone_number,
        description: formData.description
      });
      
      // Dacă utilizatorul este logat, redirecționează-l către dashboard
      if (currentUser) {
        toast.success('Obiect creat cu succes și adăugat în contul tău!');
        navigate('/dashboard');
        return;
      }
      
      // Generează URL pentru codul QR
      const qrValue = `${window.location.origin}/status/${newObject.id}`;
      
      // Stochează rezultatele
      setQrData(qrValue);
      setSeed(newObject.seed);
      
      toast.success('Codul QR a fost generat cu succes!');
    } catch (error) {
      console.error('Error creating object:', error);
      toast.error(error.response?.data?.detail || 'Nu am putut genera codul QR. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code');
    if (canvas) {
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = 'antifurt-qr.png';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
  
  const resetForm = () => {
    setFormData({
      full_name: '',
      phone_number: '',
      description: ''
    });
    setQrData(null);
    setSeed('');
  };
  
  return (
    <div className="home-container">
      <section className="hero">
        <h1>Antifurt QR</h1>
        <p>Protejează-ți obiectele personale cu un cod QR unic și un seed de siguranță.</p>
      </section>
      
      <section className="generator-section">
        <div className="row">
          <div className="col form-column">
            <h2>Generează un cod QR pentru obiectul tău</h2>
            
            {!qrData ? (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="full_name">Nume complet</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Introdu numele complet"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone_number">Număr de telefon</label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Introdu numărul de telefon"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="description">Descrierea obiectului</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descrie obiectul (tip, model, culoare, etc.)"
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn primary-btn"
                  disabled={loading}
                >
                  {loading ? 'Se generează...' : 'Generează cod QR'}
                </button>
                
                {currentUser && (
                  <p className="login-note">
                    Ești autentificat. Obiectul creat va fi asociat cu contul tău!
                  </p>
                )}
              </form>
            ) : (
              <div className="result-container">
                <h3>Codul QR a fost generat cu succes!</h3>
                
                <div className="seed-box">
                  <h4>IMPORTANT: Seed-ul tău de recuperare (12 cuvinte)</h4>
                  <p className="seed-text">{seed}</p>
                  <div className="seed-warning">
                    <strong>⚠️ Atenție:</strong> Salvează aceste cuvinte într-un loc sigur! 
                    Ele îți vor permite să îți recuperezi controlul asupra obiectului.
                  </div>
                </div>
                
                <div className="buttons-container">
                  <button onClick={downloadQRCode} className="btn primary-btn">
                    Descarcă codul QR
                  </button>
                  <button onClick={resetForm} className="btn secondary-btn">
                    Generează alt cod
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="col qr-column">
            {qrData && (
              <div className="qr-container">
                <QRCode
                  id="qr-code"
                  value={qrData}
                  size={250}
                  level="H"
                  includeMargin={true}
                />
                <p className="qr-instruction">
                  Printează acest cod QR și lipește-l pe obiectul tău.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      <section className="how-it-works">
        <h2>Cum funcționează?</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Generează QR</h3>
            <p>Introdu datele tale și obține un cod QR unic pentru obiectul tău.</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Aplică pe obiect</h3>
            <p>Printează codul QR și aplică-l pe obiectul pe care dorești să îl protejezi.</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Control total</h3>
            <p>Folosește seed-ul de 12 cuvinte pentru a controla statusul obiectului oricând.</p>
          </div>
        </div>
      </section>
      
      {/* New Mobile App Download Section */}
      <section className="app-download-section">
        <h2>Descarcă Aplicația Mobilă</h2>
        <p>Gestionează obiectele tale de oriunde, primește notificări instant și scanează coduri QR direct din aplicație.</p>
        
        <div className="app-download-container">
          <a href="#" className="app-download-link">
            <img src={appStoreIcon} alt="Download on the App Store" className="app-store-icon" />
          </a>
          <a href="#" className="app-download-link">
            <img src={googlePlayIcon} alt="Get it on Google Play" className="app-store-icon" />
          </a>
          <a href="#" className="app-download-link">
            <img src={windowsStoreIcon} alt="Get it from Microsoft Store" className="app-store-icon" />
          </a>
          <a href="#" className="app-download-link">
            <img src={macAppStoreIcon} alt="Download on the Mac App Store" className="app-store-icon" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;